import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import {
  Save, ArrowLeft, Sparkles, ChevronDown, ChevronRight, Search, Plus, X, Tag,
  Upload, Download, FileText, Check, AlertTriangle, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getTemplate, createTemplate, updateTemplate,
  getAvailableFields, aiMapPlaceholders, getTemplateTags,
  reuploadDocx, downloadOriginalDocx, matchDocxFields
} from '../../api/documentTemplates';

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id;
  const initState = location.state || {};

  const [templateName, setTemplateName] = useState(initState.name || '');
  const [description, setDescription] = useState(initState.description || '');
  const [recordTypeId, setRecordTypeId] = useState(initState.record_type_id || '');
  const [category, setCategory] = useState('general');
  const [templateTags, setTemplateTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  // DOCX template state
  const [templateType, setTemplateType] = useState('html');
  const [docxFileKey, setDocxFileKey] = useState(null);
  const [docxTextContent, setDocxTextContent] = useState('');
  const [reuploadingDocx, setReuploadingDocx] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);

  // Field sidebar state
  const [fields, setFields] = useState(null);
  const [fieldSearch, setFieldSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  // AI Import state
  const [showAIImport, setShowAIImport] = useState(initState.mode === 'import');
  const [importText, setImportText] = useState('');
  const [aiMapping, setAiMapping] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // TinyMCE editor
  const editorRef = useRef(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorReady, setEditorReady] = useState(false);

  // Load existing template
  useEffect(() => {
    if (!isNew && id) { loadTemplate(); }
    if (initState.mode === 'import') { setShowAIImport(true); }
  }, [id]);

  // Load available fields when record type changes
  useEffect(() => {
    if (recordTypeId) { loadFields(recordTypeId); }
  }, [recordTypeId]);

  const loadTemplate = async () => {
    try {
      const { data } = await getTemplate(id);
      setTemplateName(data.name);
      setDescription(data.description || '');
      setRecordTypeId(data.record_type_id);
      setCategory(data.category || 'general');
      setPlaceholders(data.placeholders || []);
      setTemplateTags((data.tags || []).map(t => t.name));
      setTemplateType(data.template_type || 'html');
      setDocxFileKey(data.docx_file_key || null);
      if (data.template_type === 'docx') {
        setDocxTextContent(data.content || '');
      }
      setEditorContent(data.content || '');
      if (initState.mode === 'import' && data.content) {
        // Strip HTML tags to get clean text
        let plainText = data.content
          .replace(/<span[^>]*data-token="([^"]*)"[^>]*>[^<]*<\/span>/g, (m, token) => token)
          .replace(/<[^>]+>/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();

        // If template already has placeholders, build mapping table from them
        const savedPlaceholders = data.placeholders || [];
        if (savedPlaceholders.length > 0) {
          // Convert {{tokens}} back to [brackets] for display
          for (const p of savedPlaceholders) {
            const bracket = '[' + (p.label || p.token.replace(/\{\{|\}\}/g, '').replace(/\./g, ' ').replace(/_/g, ' ')) + ']';
            plainText = plainText.split(p.token).join(bracket);
          }
          setImportText(plainText);
          // Pre-populate aiMapping so it shows the mapping table directly
          setAiMapping({
            mappings: savedPlaceholders.map(p => ({
              original: '[' + (p.label || p.token.replace(/\{\{|\}\}/g, '').replace(/\./g, ' ').replace(/_/g, ' ')) + ']',
              suggested_token: p.token,
              suggested_label: p.label || '',
              confidence: 'high',
              source: 'existing'
            })),
            unmapped: []
          });
        } else {
          setImportText(plainText);
        }
      }
    } catch (err) {
      toast.error('Failed to load template');
      navigate('/admin/doc-templates');
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async (rtId) => {
    try {
      const { data } = await getAvailableFields(rtId);
      setFields(data);
      const expanded = { direct: true, system: true, roles: true };
      data.linked_field_sets?.forEach((ls, i) => { expanded[`linked_${i}`] = true; });
      setExpandedSections(expanded);
    } catch (err) {
      console.error('Failed to load fields:', err);
    }
  };

  // Insert placeholder into TinyMCE
  const insertPlaceholder = useCallback((token, label) => {
    if (editorRef.current) {
      editorRef.current.insertContent(
        `<span style="background-color:#e0e7ff;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:0.85em;color:#3730a3;" data-token="${token}" contenteditable="false">${token}</span>&nbsp;`
      );
    }
    setPlaceholders(prev => {
      if (prev.find(p => p.token === token)) return prev;
      return [...prev, { token, label }];
    });
  }, []);

  // Save template
  const handleSave = async () => {
    if (!templateName.trim() || !recordTypeId) {
      toast.error('Name and record type are required');
      return;
    }
    setSaving(true);
    try {
      const content = editorRef.current ? editorRef.current.getContent() : editorContent;
      const data = { name: templateName, description, record_type_id: recordTypeId, category, content, placeholders, tags: templateTags };
      if (isNew) {
        const { data: created } = await createTemplate(data);
        toast.success('Template created!');
        navigate(`/admin/doc-templates/${created.id}/edit`, { replace: true });
      } else {
        await updateTemplate(id, data);
        toast.success('Template saved!');
      }
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // AI Import: analyze text
  const handleAIAnalyze = async () => {
    if (!importText.trim() || !recordTypeId) {
      toast.error('Paste your letter text and select a record type');
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await aiMapPlaceholders({ raw_text: importText, record_type_id: recordTypeId });
      const transformed = {
        ...data,
        mappings: (data.mappings || []).map(m => ({
          original: m.placeholder || m.original,
          suggested_token: m.token || m.suggested_token,
          suggested_label: m.field_name || m.suggested_label || '',
          confidence: m.confidence || 'medium',
          source: m.source || ''
        })),
        unmapped: data.unmapped || []
      };
      setAiMapping(transformed);
    } catch (err) {
      toast.error('AI analysis failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setAiLoading(false);
    }
  };

  // Apply AI mappings
  const applyAIMappings = (mappings) => {
    let text = importText || '';
    const sorted = [...mappings].sort((a, b) => b.original.length - a.original.length);
    for (const m of sorted) {
      if (!m.suggested_token) continue;
      text = text.split(m.original).join(m.suggested_token);
      setPlaceholders(prev => {
        if (prev.find(p => p.token === m.suggested_token)) return prev;
        return [...prev, { token: m.suggested_token, label: m.suggested_label }];
      });
    }
    const html = text.split('\n').filter(l => l.trim()).map(l => `<p>${l}</p>`).join('');
    setEditorContent(html);
    if (editorRef.current) { editorRef.current.setContent(html); }
    setShowAIImport(false);
    setAiMapping(null);
    toast.success('Template imported! Review and edit as needed.');
  };

  const filterFields = (fieldList) => {
    if (!fieldSearch) return fieldList;
    return fieldList.filter(f => f.name.toLowerCase().includes(fieldSearch.toLowerCase()));
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // DOCX-specific handlers
  const handleDownloadDocx = async () => {
    try {
      const { data } = await downloadOriginalDocx(id);
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = templateName.replace(/[^a-zA-Z0-9]/g, '_') + '.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleReuploadDocx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReuploadingDocx(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await reuploadDocx(id, formData);
      setPlaceholders(data.placeholders || []);
      setDocxFileKey(data.docx_file_key);
      setDocxTextContent(data.content || '');
      toast.success('Template file updated!');
    } catch (err) {
      toast.error('Re-upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setReuploadingDocx(false);
      e.target.value = '';
    }
  };

  // Auto-match DOCX placeholders to fields using fuzzy matching
  const handleAutoMatch = async () => {
    if (!recordTypeId || placeholders.length === 0) return;
    setAutoMatching(true);
    try {
      const { data } = await matchDocxFields({
        record_type_id: recordTypeId,
        placeholders: placeholders.map(p => ({ name: p.name, context: p.context || '' }))
      });
      // Merge results into placeholders
      const updated = placeholders.map(p => {
        const match = (data.mappings || []).find(m => m.name === p.name);
        if (match && match.mapped_to) {
          return { ...p, mapped_to: match.mapped_to, field_id: match.field_id, confidence: match.confidence };
        }
        return p;
      });
      setPlaceholders(updated);
      const matched = updated.filter(p => p.mapped_to).length;
      toast.success(`Auto-matched ${matched} of ${updated.length} placeholders`);
    } catch (err) {
      toast.error('Auto-match failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setAutoMatching(false);
    }
  };

  // Save placeholder mappings
  const handleSaveMappings = async () => {
    setSavingMappings(true);
    try {
      await updateTemplate(id, { placeholders });
      toast.success('Mappings saved!');
    } catch (err) {
      toast.error('Failed to save mappings');
    } finally {
      setSavingMappings(false);
    }
  };

  // Update a single placeholder mapping
  const updatePlaceholderMapping = (index, token, label) => {
    setPlaceholders(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        mapped_to: token || null,
        field_id: null,
        confidence: token ? 'manual' : 'none'
      };
      return updated;
    });
  };

  // Check placeholder match status against available fields
  const getPlaceholderStatus = (placeholder) => {
    if (!fields) return 'unknown';
    const token = placeholder.token || `{{${placeholder}}}`;
    const bare = token.replace(/^\{\{/, '').replace(/\}\}$/, '');

    // Check system variables
    if (fields.system_variables?.some(sv => sv.token === token)) return 'matched';

    // Check direct fields
    if (fields.direct_fields?.some(f => f.name === bare)) return 'matched';

    // Check linked fields
    if (bare.includes('.')) {
      const [prefix, fieldName] = bare.split('.');
      for (const ls of (fields.linked_field_sets || [])) {
        const linkPrefix = (ls.link_label || '').toLowerCase().replace(/\s+/g, '_');
        if (linkPrefix === prefix && ls.fields?.some(f => f.name === fieldName)) return 'matched';
      }
      // Check role fields
      for (const role of (fields.role_fields || [])) {
        if (role.prefix === prefix && role.fields?.some(f => f.path === bare)) return 'matched';
      }
    }

    return 'unmatched';
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading template...</div>;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
        <button onClick={() => navigate('/admin/doc-templates')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Template name..." className="text-lg font-semibold bg-transparent border-none focus:outline-none flex-1" />

        {templateType === 'docx' && (
          <span className="px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full border border-teal-200">DOCX</span>
        )}

        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
          <option value="general">General</option>
          <option value="letter">Letter</option>
          <option value="legal">Legal</option>
          <option value="intake">Intake</option>
          <option value="billing">Billing</option>
        </select>

        {/* Tag picker */}
        <div className="relative">
          <div className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-lg min-w-[200px] max-w-[360px] flex-wrap bg-white">
            {templateTags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                {tag}
                <button onClick={() => setTemplateTags(prev => prev.filter(t => t !== tag))} className="hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={tagInput}
              onChange={async (e) => {
                setTagInput(e.target.value);
                setShowTagDropdown(true);
                try {
                  const { data } = await getTemplateTags(e.target.value);
                  setTagSuggestions(data.filter(t => !templateTags.some(existing => existing.toLowerCase() === t.name.toLowerCase())));
                } catch (err) { setTagSuggestions([]); }
              }}
              onFocus={async () => {
                setShowTagDropdown(true);
                try {
                  const { data } = await getTemplateTags('');
                  setTagSuggestions(data.filter(t => !templateTags.some(existing => existing.toLowerCase() === t.name.toLowerCase())));
                } catch (err) { setTagSuggestions([]); }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && tagInput.trim()) {
                  e.preventDefault();
                  const val = tagInput.trim();
                  if (!templateTags.some(t => t.toLowerCase() === val.toLowerCase())) {
                    setTemplateTags(prev => [...prev, val]);
                  }
                  setTagInput('');
                  setShowTagDropdown(false);
                }
              }}
              placeholder={templateTags.length === 0 ? "Add tags..." : ""}
              className="flex-1 min-w-[60px] text-sm bg-transparent border-none focus:outline-none py-0.5"
            />
            <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          </div>
          {showTagDropdown && (tagSuggestions.length > 0 || tagInput.trim()) && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
              onMouseDown={(e) => e.preventDefault()}>
              {tagSuggestions.map(t => (
                <button key={t.id} onClick={() => {
                  if (!templateTags.some(existing => existing.toLowerCase() === t.name.toLowerCase())) {
                    setTemplateTags(prev => [...prev, t.name]);
                  }
                  setTagInput('');
                  setShowTagDropdown(false);
                }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: t.color }} />
                  {t.name}
                </button>
              ))}
              {tagInput.trim() && !tagSuggestions.some(s => s.name.toLowerCase() === tagInput.trim().toLowerCase()) && (
                <button onClick={() => {
                  const val = tagInput.trim();
                  if (!templateTags.some(t => t.toLowerCase() === val.toLowerCase())) {
                    setTemplateTags(prev => [...prev, val]);
                  }
                  setTagInput('');
                  setShowTagDropdown(false);
                }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-600 flex items-center gap-2 border-t">
                  <Plus className="w-3.5 h-3.5" /> Create &ldquo;{tagInput.trim()}&rdquo;
                </button>
              )}
            </div>
          )}
        </div>

        {templateType !== 'docx' && (
          <button onClick={() => setShowAIImport(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 text-sm">
            <Sparkles className="w-4 h-4" /> AI Import
          </button>
        )}
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {templateType === 'docx' ? (
          /* ============================== */
          /* DOCX EDITOR VIEW               */
          /* ============================== */
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col h-full">
                {/* Top section: File info + Description */}
                <div className="bg-white border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {docxFileKey ? docxFileKey.split('/').pop() : 'No file uploaded'}
                        </p>
                        <input value={description} onChange={e => setDescription(e.target.value)}
                          placeholder="Template description..."
                          className="text-xs text-gray-400 bg-transparent border-none focus:outline-none w-full mt-0.5" />
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={handleDownloadDocx}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600">
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                      <label className={`flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-xs text-gray-600 cursor-pointer ${reuploadingDocx ? 'opacity-50' : ''}`}>
                        <RefreshCw className={`w-3.5 h-3.5 ${reuploadingDocx ? 'animate-spin' : ''}`} />
                        {reuploadingDocx ? 'Uploading...' : 'Re-upload'}
                        <input type="file" accept=".docx" onChange={handleReuploadDocx} className="hidden" disabled={reuploadingDocx} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Two-panel layout: Document Preview + Placeholder Mappings */}
                <div className="flex flex-1 overflow-hidden">
                  {/* Left: Document Text Preview */}
                  <div className="w-1/2 border-r border-gray-200 overflow-y-auto bg-white">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Document Preview</h3>
                    </div>
                    <div className="p-5">
                      {docxTextContent ? (
                        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-mono"
                          dangerouslySetInnerHTML={{
                            __html: docxTextContent.replace(
                              /\{\{([^}]+)\}\}/g,
                              '<mark style="background:#dbeafe;padding:1px 4px;border-radius:3px;color:#1e40af;font-weight:500">{​{$1}​}</mark>'
                            )
                          }} />
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-8">No document content extracted yet. Try re-uploading the .docx file.</p>
                      )}
                    </div>
                  </div>

                  {/* Right: Placeholder Mapping Table */}
                  <div className="w-1/2 overflow-y-auto bg-gray-50">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Placeholder Mappings ({placeholders.length})
                      </h3>
                      <div className="flex gap-2">
                        <button onClick={handleAutoMatch} disabled={autoMatching || !recordTypeId || placeholders.length === 0}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 text-xs font-medium disabled:opacity-40">
                          {autoMatching ? (
                            <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Matching...</>
                          ) : (
                            <><Sparkles className="w-3.5 h-3.5" /> Auto-Match</>
                          )}
                        </button>
                        <button onClick={handleSaveMappings} disabled={savingMappings}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50">
                          {savingMappings ? (
                            <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="w-3.5 h-3.5" /> Save Mappings</>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      {placeholders.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">No placeholders found in this document</p>
                      ) : (
                        <div className="space-y-3">
                          {placeholders.map((p, i) => {
                            const isMapped = !!p.mapped_to;
                            return (
                              <div key={i} className={`bg-white rounded-xl border p-4 ${isMapped ? 'border-green-200' : 'border-gray-200'}`}>
                                <div className="flex items-start gap-3 mb-2">
                                  {isMapped ? (
                                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <code className="text-sm font-mono font-semibold text-gray-800">{`{{${p.name}}}`}</code>
                                    {p.context && (
                                      <p className="text-[11px] text-gray-400 mt-1 truncate" title={p.context}>
                                        {p.context}
                                      </p>
                                    )}
                                  </div>
                                  {p.confidence && p.confidence !== 'none' && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                      p.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                      p.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                      p.confidence === 'manual' ? 'bg-blue-100 text-blue-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {p.confidence}
                                    </span>
                                  )}
                                </div>
                                <div className="ml-7">
                                  <FieldSelect
                                    value={p.mapped_to || ''}
                                    fields={fields}
                                    onChange={(token, label) => updatePlaceholderMapping(i, token, label)}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* ============================== */
          /* HTML EDITOR VIEW (unchanged)   */
          /* ============================== */
          <>
            {/* Editor */}
            <div className="flex-1 overflow-y-auto bg-white border-r border-gray-200">
              <Editor
                tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.4/tinymce.min.js"
                onInit={(evt, editor) => { editorRef.current = editor; setEditorReady(true); }}
                initialValue={editorContent}
                init={{
                  height: '100%',
                  menubar: true,
                  branding: false,
                  promotion: false,
                  statusbar: true,
                  resize: false,
                  skin: 'oxide',
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'help', 'wordcount', 'pagebreak',
                    'emoticons', 'codesample'
                  ],
                  toolbar: [
                    'undo redo | styles fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor',
                    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table link image pagebreak | removeformat fullscreen code'
                  ],
                  font_family_formats: 'Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times,serif; Georgia=georgia,serif; Courier New=courier new,courier,monospace; Verdana=verdana,geneva,sans-serif; Garamond=garamond,serif; Calibri=calibri,sans-serif; Trebuchet MS=trebuchet ms,helvetica,sans-serif; Book Antiqua=book antiqua,palatino,serif;',
                  font_size_formats: '8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 32pt 36pt 48pt',
                  content_style: `
                    body {
                      font-family: 'Times New Roman', serif;
                      font-size: 12pt;
                      line-height: 1.6;
                      padding: 40px 60px;
                      max-width: 800px;
                      margin: 0 auto;
                    }
                    p { margin: 0 0 6pt 0; }
                    table { border-collapse: collapse; width: 100%; }
                    td, th { border: 1px solid #ccc; padding: 6px 10px; }
                    th { background: #f5f5f5; font-weight: 600; }
                  `,
                  table_default_styles: { 'border-collapse': 'collapse', 'width': '100%' },
                  table_responsive_width: true,
                  setup: (editor) => {
                    editor.on('change keyup', () => {
                      setEditorContent(editor.getContent());
                    });
                  }
                }}
              />
            </div>

            {/* Field Sidebar */}
            <div className="w-80 bg-gray-50 border-l border-gray-200 overflow-y-auto flex-shrink-0">
              <div className="p-4">
                <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Available Fields</h3>
                <p className="text-xs text-gray-400 mb-3">Click a field to insert it into the editor</p>
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                  <input type="text" placeholder="Search fields..." value={fieldSearch}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                {!fields ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    {recordTypeId ? 'Loading fields...' : 'Select a record type first'}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {fields.direct_fields?.length > 0 && (
                      <FieldSection title="📋 Record Fields" sectionKey="direct" expanded={expandedSections.direct} onToggle={() => toggleSection('direct')}>
                        {filterFields(fields.direct_fields).map(f => (
                          <FieldItem key={f.id} name={f.name} type={f.field_type}
                            onClick={() => insertPlaceholder(`{{${f.name}}}`, f.name.replace(/_/g, ' '))} />
                        ))}
                      </FieldSection>
                    )}
                    {fields.linked_field_sets?.map((linkSet, i) => (
                      <FieldSection key={i} title={`👤 ${linkSet.link_label} (${linkSet.target_record_type})`}
                        sectionKey={`linked_${i}`} expanded={expandedSections[`linked_${i}`]}
                        onToggle={() => toggleSection(`linked_${i}`)}>
                        {filterFields(linkSet.fields).map(f => {
                          const prefix = linkSet.link_label.toLowerCase().replace(/\s+/g, '_');
                          return (
                            <FieldItem key={f.id} name={f.name} type={f.field_type}
                              label={`${linkSet.link_label} → ${f.name.replace(/_/g, ' ')}`}
                              onClick={() => insertPlaceholder(`{{${prefix}.${f.name}}}`, `${linkSet.link_label} → ${f.name.replace(/_/g, ' ')}`)} />
                          );
                        })}
                      </FieldSection>
                    ))}
                    {fields.role_fields?.length > 0 && (
                      <FieldSection title="👥 Team Roles" sectionKey="roles" expanded={expandedSections.roles} onToggle={() => toggleSection('roles')}>
                        {fields.role_fields.filter(role =>
                          !fieldSearch || role.role_name.toLowerCase().includes(fieldSearch.toLowerCase()) ||
                          role.fields.some(f => f.label.toLowerCase().includes(fieldSearch.toLowerCase()))
                        ).map(role => (
                          <div key={role.role_id} className="mb-1">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-1">{role.role_name}</p>
                            {role.fields.map(f => (
                              <FieldItem key={f.path} name={f.label} type="role"
                                onClick={() => insertPlaceholder(f.token, f.label)} />
                            ))}
                          </div>
                        ))}
                      </FieldSection>
                    )}
                    <FieldSection title="⚙️ System Variables" sectionKey="system" expanded={expandedSections.system} onToggle={() => toggleSection('system')}>
                      {fields.system_variables?.map((sv, i) => (
                        <FieldItem key={i} name={sv.label} type="system" onClick={() => insertPlaceholder(sv.token, sv.label)} />
                      ))}
                    </FieldSection>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* AI Import Modal (HTML only) */}
      {showAIImport && templateType !== 'docx' && (
        <AIImportModal importText={importText} setImportText={setImportText} aiMapping={aiMapping}
          recordTypeId={recordTypeId} aiLoading={aiLoading} onAnalyze={handleAIAnalyze}
          onApply={applyAIMappings} onClose={() => { setShowAIImport(false); setAiMapping(null); }}
          onBack={() => setAiMapping(null)} fields={fields} />
      )}
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================
function FieldSection({ title, sectionKey, expanded, onToggle, children }) {
  return (
    <div className="mb-1">
      <button onClick={onToggle} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
      </button>
      {expanded && <div className="ml-2 pl-2 border-l border-gray-200 space-y-0.5">{children}</div>}
    </div>
  );
}

function FieldItem({ name, type, label, onClick }) {
  const displayName = label || name.replace(/_/g, ' ');
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between px-2 py-1 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-700 rounded group">
      <span className="truncate capitalize">{displayName}</span>
      <Plus className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 flex-shrink-0" />
    </button>
  );
}

function DocxFieldRef({ token }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="w-full flex items-center justify-between px-2 py-1 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-700 rounded group" title="Click to copy">
      <code className="text-xs font-mono truncate">{token}</code>
      <span className="text-[10px] text-gray-300 group-hover:text-teal-500 flex-shrink-0">{copied ? 'Copied!' : 'Copy'}</span>
    </button>
  );
}

// ============================================================
// Searchable Field Select for AI Import
// ============================================================
function FieldSelect({ value, fields, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!fields) return <span className="text-xs text-gray-400">No fields loaded</span>;

  const options = [];
  if (fields.system_variables) {
    for (const sv of fields.system_variables) { options.push({ token: sv.token, label: sv.label, group: 'System' }); }
  }
  if (fields.direct_fields) {
    const rtName = fields.direct_fields[0]?.record_type_name || 'Record';
    for (const f of fields.direct_fields) { options.push({ token: '{{' + f.name + '}}', label: f.name, group: rtName }); }
  }
  if (fields.linked_field_sets) {
    for (const set of fields.linked_field_sets) {
      const prefix = (set.link_label || set.link_field_name).toLowerCase().replace(/\s+/g, '_');
      for (const f of set.fields) { options.push({ token: '{{' + prefix + '.' + f.name + '}}', label: f.name, group: set.target_record_type || set.link_label }); }
    }
  }
  if (fields.role_fields) {
    for (const role of fields.role_fields) {
      for (const f of role.fields) { options.push({ token: f.token, label: f.label, group: 'Role: ' + role.role_name }); }
    }
  }

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()) || o.token.toLowerCase().includes(search.toLowerCase()) || o.group.toLowerCase().includes(search.toLowerCase()))
    : options;

  const groups = {};
  for (const o of filtered) { if (!groups[o.group]) groups[o.group] = []; groups[o.group].push(o); }

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)}
        className={`w-full text-left px-2 py-1.5 border rounded text-sm flex items-center justify-between ${value ? 'border-green-300 bg-green-50 text-green-800' : 'border-gray-200 text-gray-400'}`}>
        <span className="truncate">{value ? value : 'Select field...'}</span>
        <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-80 bg-white rounded-lg shadow-xl border border-gray-200 max-h-64 flex flex-col">
          <div className="p-2 border-b">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search fields..."
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" autoFocus />
          </div>
          <div className="overflow-y-auto flex-1">
            {Object.keys(groups).length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No fields found</p>}
            <button onClick={() => { onChange('', ''); setOpen(false); setSearch(''); }}
              className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50">✕ Clear mapping</button>
            {Object.entries(groups).map(([group, items]) => (
              <div key={group}>
                <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase bg-gray-50 sticky top-0">{group}</div>
                {items.map(o => (
                  <button key={o.token} onClick={() => { onChange(o.token, o.label); setOpen(false); setSearch(''); }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 flex items-center justify-between ${o.token === value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}>
                    <span className="truncate">{o.label}</span>
                    <span className="text-[10px] text-gray-400 ml-2 flex-shrink-0">{o.token}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// AI Import Modal
// ============================================================
function AIImportModal({ importText, setImportText, aiMapping, aiLoading, onAnalyze, onApply, onClose, onBack, fields, recordTypeId }) {
  const [editedMappings, setEditedMappings] = useState([]);
  const [dryRunMode, setDryRunMode] = useState(false);
  const [dryRunRecords, setDryRunRecords] = useState([]);
  const [dryRunRecordId, setDryRunRecordId] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  useEffect(() => {
    if (aiMapping?.mappings) { setEditedMappings(aiMapping.mappings); }
  }, [aiMapping]);

  const loadDryRunRecords = async () => {
    if (!recordTypeId) return;
    try {
      const { default: cl } = await import('../../api/client');
      const { data } = await cl.get('/records', { params: { record_type_id: recordTypeId, limit: 50 } });
      setDryRunRecords(data.records || []);
    } catch (err) { console.error(err); }
  };

  const runDryRun = async () => {
    if (!dryRunRecordId) return;
    setDryRunLoading(true);
    try {
      const { default: cl } = await import('../../api/client');
      const { data } = await cl.post('/document-templates/dry-run', {
        template_text: importText, mappings: editedMappings, record_id: dryRunRecordId
      });
      setDryRunResult(data);
    } catch (err) { console.error(err); }
    finally { setDryRunLoading(false); }
  };

  const buildPreviewHtml = () => {
    if (!dryRunResult) return '';
    let text = importText;
    // Build set of resolved placeholders
    const resolvedSet = new Set(Object.keys(dryRunResult.resolved));
    const sorted = Object.entries(dryRunResult.resolved).sort((a, b) => b[0].length - a[0].length);
    for (const [orig, { value }] of sorted) {
      const hasVal = value && value !== '___________';
      const hl = hasVal
        ? '<mark style="background:#bbf7d0;padding:2px 4px;border-radius:3px;font-weight:500">' + value + '</mark>'
        : '<mark style="background:#fef3c7;padding:2px 4px;border-radius:3px;color:#92400e" title="Mapped but empty in this record">' + orig + ' ⚠️</mark>';
      text = text.split(orig).join(hl);
    }
    // Highlight remaining [brackets] as unmapped (red)
    text = text.replace(/\[([^\[\]]+)\]/g, (match) => {
      return '<mark style="background:#fecaca;padding:2px 4px;border-radius:3px;color:#991b1b" title="Not mapped to any field">' + match + ' ❌</mark>';
    });
    return text.replace(/\n/g, '<br>');
  };

  const updateMapping = (index, field, value) => {
    setEditedMappings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const confidenceColors = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-red-100 text-red-700',
    manual: 'bg-blue-100 text-blue-700',
    none: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold">AI Template Import</h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {!aiMapping ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Paste your letter or document text below. Use <code className="bg-gray-100 px-1 rounded">[brackets]</code> around placeholders.
              </p>
              <textarea value={importText} onChange={(e) => setImportText(e.target.value)}
                placeholder={`Example:\n\nDear [Client Name],\n\nI'm [Your Name] from [Your Company].\n\nBest regards,\n[Your Name]`}
                className="w-full h-64 p-4 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none" />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">AI found <strong>{editedMappings.length}</strong> placeholders. Review and adjust the mappings below:</p>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Found in Text</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Mapped To</th>
                      <th className="text-center px-4 py-2 font-medium text-gray-600 w-24">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedMappings.map((m, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-4 py-3"><code className="bg-gray-100 px-2 py-0.5 rounded text-gray-700">{m.original}</code></td>
                        <td className="px-4 py-3">
                          <FieldSelect value={m.suggested_token || ''} fields={fields}
                            onChange={(token, label) => { updateMapping(i, 'suggested_token', token); updateMapping(i, 'suggested_label', label); updateMapping(i, 'confidence', token ? 'manual' : 'none'); }} />
                          {m.suggested_label && <p className="text-xs text-gray-400 mt-1">{m.suggested_label}</p>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${confidenceColors[m.confidence] || confidenceColors.none}`}>{m.confidence}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {/* Preview Panel */}
        {dryRunMode && aiMapping && (
          <div className="px-6 py-4 border-t bg-blue-50">
            <div className="flex items-center gap-3 mb-3">
              <h4 className="text-sm font-semibold text-blue-800">🔍 Preview</h4>
              <select value={dryRunRecordId} onChange={e => setDryRunRecordId(e.target.value)}
                className="px-2 py-1.5 border border-blue-200 rounded-lg text-sm bg-white flex-1 max-w-xs">
                <option value="">Select a record...</option>
                {dryRunRecords.map(r => (<option key={r.id} value={r.id}>{r.reference_number ? r.reference_number + ' — ' : ''}{r.title}</option>))}
              </select>
              <button onClick={runDryRun} disabled={!dryRunRecordId || dryRunLoading}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40">
                {dryRunLoading ? 'Loading...' : 'Preview'}
              </button>
            </div>
            {dryRunResult && (
              <div className="bg-white rounded-lg border border-blue-200 p-4 max-h-64 overflow-auto">
                <div className="flex items-center gap-4 mb-2 text-xs">
                  <span className="flex items-center gap-1"><span style={{background:'#bbf7d0',padding:'2px 6px',borderRadius:3}}>●</span> Has value</span>
                  <span className="flex items-center gap-1"><span style={{background:'#fef3c7',padding:'2px 6px',borderRadius:3}}>●</span> Mapped, no data</span>
                  <span className="flex items-center gap-1"><span style={{background:'#fecaca',padding:'2px 6px',borderRadius:3}}>●</span> Not mapped</span>
                  <span className="text-gray-400">Record: {dryRunResult.record_title || dryRunResult.reference_number}</span>
                </div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-mono" dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }} />
              </div>
            )}
            {!dryRunResult && <p className="text-xs text-blue-600">Select a record and click Preview to see how the template looks with real data</p>}
          </div>
        )}
        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          {!aiMapping ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={onAnalyze} disabled={aiLoading || !importText.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {aiLoading ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Analyzing...</>)
                  : (<><Sparkles className="w-4 h-4" />Analyze with AI</>)}
              </button>
            </>
          ) : (
            <>
              <button onClick={onBack} className="px-4 py-2 text-gray-600 hover:text-gray-800">Back</button>
              <button type="button" onClick={() => { if (!dryRunMode) { setDryRunMode(true); loadDryRunRecords(); } else { setDryRunMode(false); } }}
                className={`px-4 py-2 border rounded-lg text-sm cursor-pointer ${dryRunMode ? 'bg-blue-100 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                🔍 Preview
              </button>
              <button onClick={() => onApply(editedMappings)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Apply Mappings
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
